"use client"

import { useCallback, useRef } from "react"

export function useSound(
  soundUrl: string,
  { volume = 1, soundEnabled = true }: { volume?: number; soundEnabled?: boolean },
) {
  // Track if the sound file is available (cached to avoid repeated failed requests)
  const soundAvailableRef = useRef<boolean | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Memoize the play function
  const play = useCallback(() => {
    if (!soundEnabled || !soundUrl) return

    // If we've already determined the sound is unavailable, don't try again
    if (soundAvailableRef.current === false) return

    try {
      // Create a new Audio object on each play to allow for overlapping sounds
      const newAudio = new Audio(soundUrl)
      newAudio.volume = volume

      // Handle load errors silently (sound files are optional UX enhancements)
      const handleError = () => {
        soundAvailableRef.current = false
        // Silently fail - sounds are optional
      }

      // Handle successful load
      const handleCanPlay = () => {
        soundAvailableRef.current = true
      }

      newAudio.addEventListener('error', handleError, { once: true })
      newAudio.addEventListener('canplay', handleCanPlay, { once: true })

      // Attempt to play, but catch errors silently
      newAudio.play().catch(() => {
        // Silently fail - sounds are optional UX enhancements
        soundAvailableRef.current = false
      })
    } catch (error) {
      // Silently fail if Audio constructor fails
      soundAvailableRef.current = false
    }
  }, [soundUrl, volume, soundEnabled])

  return play
}